import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';

const C = {
  bg:'#06070b', bg2:'#0d1118', bg3:'#141a24',
  blue:'#4ba3ff', blueL:'rgba(75,163,255,0.14)', blueB:'rgba(109,178,255,0.2)',
  white:'#ffffff', grey:'#9ea6b4', grey2:'#d7dde8',
  green:'#46d369', red:'#ff5a54', gold:'#f1c66d', goldL:'rgba(241,198,109,0.12)',
  border:'rgba(176,202,233,0.12)',
};

const STATS = [
  {val:'R49', label:'Launch price for the first 6 months'},
  {val:'Soon', label:'Launch lineup taking shape now'},
  {val:'Live', label:'Watch-alongs and special events coming soon'},
  {val:'SA', label:'Originals, podcasts and local stories'},
];

const VIEWER_BENEFITS = [
  {title:'South African stories first', desc:'Originals, talk shows, sports energy and podcasts rooted in what South Africans actually care about.'},
  {title:'Live culture, planned for launch', desc:'Watch-alongs, match reactions, creator livestreams and weekly drops are part of the launch plan and will roll out as VAULTT opens.'},
  {title:'Affordable launch pricing', desc:'Join at R49 per month for the first 6 months after launch and get in before the platform opens fully.'},
  {title:'A platform with personality', desc:'Editorial picks, trending shelves and creator-led communities instead of an endless wall of forgettable content.'},
];

const TRENDING_SHOWS = [
  {title:'Coming Soon', meta:'SA Originals', blurb:'A new slate of South African originals is being prepared for launch.', accent:'#1f4f8f', badge:'Soon'},
  {title:'Coming Soon', meta:'Sports', blurb:'Watch-alongs, match reactions and sports energy are coming to VAULTT soon.', accent:'#1b5c2e', badge:'Soon'},
  {title:'Coming Soon', meta:'Talk Shows', blurb:'Sharp conversations, panels and creator-led discussions are on the way.', accent:'#6b3410', badge:'Soon'},
  {title:'Coming Soon', meta:'Podcasts', blurb:'Video podcasts with real South African perspective are coming soon.', accent:'#173d74', badge:'Soon'},
];

const LIVE_NOW = [
  {title:'Coming Soon', host:'Live watch-alongs', viewers:'Launch programming in progress', accent:'#215c2b'},
  {title:'Coming Soon', host:'Creator livestreams', viewers:'Premiere schedule coming soon', accent:'#163c72'},
  {title:'Coming Soon', host:'Special live events', viewers:'First dates to be announced', accent:'#5a2c67'},
];

const EDITORIAL_SHELVES = [
  {title:'At Launch', items:['First original drops', 'Opening live formats', 'Founding viewer updates']},
  {title:'Planned for VAULTT', items:['SA Originals', 'Creator exclusives', 'Founding-era releases']},
  {title:'What to Expect', items:['Launch announcements', 'Editor spotlights', 'Early access updates']},
];

const PILLARS = [
  {icon:'\u{1F1FF}\u{1F1E6}', title:'SA Originals', tag:'Series', accent:'linear-gradient(135deg, rgba(241,198,109,0.26), rgba(75,163,255,0.1))', desc:'Long-form drama, reality, lifestyle and comedy made with a premium South African point of view.'},
  {icon:'\u26BD', title:'Sports', tag:'Live energy', accent:'linear-gradient(135deg, rgba(104,196,111,0.24), rgba(75,163,255,0.08))', desc:'Watch-alongs, pre-match analysis and post-match breakdowns for PSL, Springboks and more.'},
  {icon:'\u{1F399}', title:'Talk Shows', tag:'Voices', accent:'linear-gradient(135deg, rgba(255,173,112,0.24), rgba(255,255,255,0.06))', desc:'Panel discussions, interviews and current affairs built around sharp South African voices.'},
  {icon:'\u{1F3A7}', title:'Podcasts', tag:'Weekly drops', accent:'linear-gradient(135deg, rgba(155,191,255,0.24), rgba(255,255,255,0.06))', desc:'Video podcasts with loyal followings, recurring episodes and direct supporter relationships.'},
];

const FAQS = [
  {q:'How do I become a creator on VAULTT?', a:'VAULTT is a curated platform — all creators apply and are personally reviewed. Click Apply as Founding Creator and fill in the form. We respond within 3 business days.'},
  {q:'How does the revenue pool work?', a:'75% of all subscription revenue goes into the Creator Pool. It is distributed monthly based on watch time. The more your content is watched, the more you earn. Founding creators keep 75% for their first 12 months.'},
  {q:'What kind of content can I upload?', a:'VAULTT accepts SA Originals (drama, reality, lifestyle, comedy), Sports content (watch-alongs, analysis), Talk Shows and Video Podcasts. All content must be original and created by you.'},
  {q:'How much does it cost to join as a creator?', a:'Joining VAULTT as a creator is completely free. You keep 75% of all revenue your content generates. There are no upfront fees, no monthly charges and no hidden costs.'},
  {q:'When does VAULTT launch?', a:'VAULTT launches in June 2026. Founding creator applications are open now. Approved creators will be onboarded before launch so your content is ready from day one.'},
  {q:'What equipment do I need?', a:'A smartphone and good lighting is enough to start. VAULTT is built for authentic SA content, not Hollywood productions. We care about your voice and your story, not your camera budget.'},
];

const BROWSE_ROWS = [
  {
    title:'On the Way to VAULTT',
    badge:'Coming Soon',
    items:[
      {rank:'1', title:'Original Stories', subtitle:'Drama & reality', meta:'Series • Premium local stories', accent:'linear-gradient(180deg,#8091af 0%,#2f4267 48%,#131823 100%)', aura:'radial-gradient(circle at 72% 20%, rgba(255,255,255,0.32), transparent 34%)', glyph:'VS', stamp:'New slate'},
      {rank:'2', title:'PSL Energy', subtitle:'Watch-alongs', meta:'Live • Matchday culture', accent:'linear-gradient(180deg,#57783a 0%,#28411d 46%,#121813 100%)', aura:'radial-gradient(circle at 76% 16%, rgba(197,255,160,0.28), transparent 32%)', glyph:'90', stamp:'Live soon'},
      {rank:'3', title:'Real Talk', subtitle:'Panels & interviews', meta:'Talk • Sharp conversations', accent:'linear-gradient(180deg,#93654b 0%,#553626 48%,#17110d 100%)', aura:'radial-gradient(circle at 76% 18%, rgba(255,214,178,0.28), transparent 32%)', glyph:'RT', stamp:'Studio'},
      {rank:'4', title:'Video Podcasts', subtitle:'New voices', meta:'Weekly • Creator-led formats', accent:'linear-gradient(180deg,#40658e 0%,#223a5f 48%,#121720 100%)', aura:'radial-gradient(circle at 76% 18%, rgba(151,212,255,0.28), transparent 32%)', glyph:'ON', stamp:'Weekly'},
      {rank:'5', title:'Creator Drops', subtitle:'Member moments', meta:'Bonus • Founding access', accent:'linear-gradient(180deg,#786d86 0%,#3f3649 48%,#17161b 100%)', aura:'radial-gradient(circle at 78% 20%, rgba(238,213,255,0.26), transparent 32%)', glyph:'FD', stamp:'Bonus'},
    ],
  },
  {
    title:'What VAULTT Is Building',
    badge:'Launch Slate',
    items:[
      {rank:'1', title:'SA Originals', subtitle:'Premium local stories', meta:'Drama • Reality • Lifestyle', accent:'linear-gradient(180deg,#9a6554 0%,#5b3329 48%,#181114 100%)', aura:'radial-gradient(circle at 74% 18%, rgba(255,195,173,0.3), transparent 34%)', glyph:'SA', stamp:'Original'},
      {rank:'2', title:'Sports Talk', subtitle:'Before, during and after', meta:'Football • Rugby • Fan voices', accent:'linear-gradient(180deg,#44753a 0%,#233f1f 48%,#121813 100%)', aura:'radial-gradient(circle at 76% 18%, rgba(196,255,178,0.26), transparent 32%)', glyph:'ST', stamp:'Matchday'},
      {rank:'3', title:'Culture Shows', subtitle:'Current voices', meta:'Panels • Interviews • Opinion', accent:'linear-gradient(180deg,#734f79 0%,#422a47 48%,#17131b 100%)', aura:'radial-gradient(circle at 78% 18%, rgba(235,188,255,0.24), transparent 32%)', glyph:'CV', stamp:'Voices'},
      {rank:'4', title:'Live Moments', subtitle:'Special events', meta:'Watch-alongs • Premieres', accent:'linear-gradient(180deg,#3f687a 0%,#22404d 48%,#11181d 100%)', aura:'radial-gradient(circle at 76% 18%, rgba(165,229,255,0.26), transparent 32%)', glyph:'LM', stamp:'Event'},
      {rank:'5', title:'Founding Access', subtitle:'Early supporters first', meta:'Updates • Drops • Perks', accent:'linear-gradient(180deg,#7c7684 0%,#45404c 48%,#17171b 100%)', aura:'radial-gradient(circle at 76% 20%, rgba(255,255,255,0.18), transparent 30%)', glyph:'FA', stamp:'Access'},
    ],
  },
];

const FEATURE_STRIPS = [
  {eyebrow:'For Viewers', title:'A premium South African watchlist.', copy:'Originals, talk formats, watch-alongs and podcasts are being lined up for launch in one calm, premium experience.'},
  {eyebrow:'For Creators', title:'A better business home.', copy:'Memberships, bonus drops, tips and direct supporter relationships give creators more than just another upload destination.'},
  {eyebrow:'For Launch', title:'Built for momentum.', copy:'Founding pricing, launch updates, early access and curated channels are all focused on building a stronger day-one lineup.'},
];

const TRUST_FEATURES = [
  {
    eyebrow:'Copyright Safe',
    title:'A platform that protects original work.',
    copy:'VAULTT’s Copyright & Content Policy makes it clear that creators keep ownership, third-party rights must be cleared, and infringement complaints have a formal process.',
    cta:'Read Copyright Policy',
    href:'/copyright.html',
  },
  {
    eyebrow:'Creator Rights',
    title:'Ownership stays with the creator.',
    copy:'Founding creators do not hand over their intellectual property. VAULTT receives only the platform licence needed to host, stream and promote content.',
    cta:'Apply as Creator',
    action:'apply',
  },
];

function useInView(threshold) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if(e.isIntersecting) setInView(true); }, {threshold: threshold || 0.1});
    if(ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Section({children, style}) {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} style={{opacity: inView?1:0, transform: inView?'translateY(0)':'translateY(40px)', transition:'all 0.7s ease', ...style}}>
      {children}
    </div>
  );
}

export default function LandingPage({onSignIn, onApply}) {
  const [openFaq, setOpenFaq] = useState(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [interest, setInterest] = useState('SA Originals');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleEarlyAccess = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!normalizedEmail) {
      setSubmitError('Please enter your email address.');
      return;
    }

    if (!emailPattern.test(normalizedEmail)) {
      setSubmitError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const { error } = await supabase
      .from('viewer_waitlist')
      .insert({
        email: normalizedEmail,
        full_name: fullName.trim() || null,
        interest,
        source: 'landing_page',
      });

    if (error) {
      if (error.code === '23505') {
        setSubmitted(true);
        setSubmitError('');
      } else {
        setSubmitError('We could not save your details right now. Please try again.');
      }
      setSubmitting(false);
      return;
    }

    fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-viewer-waitlist`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: normalizedEmail,
        full_name: fullName.trim() || null,
        interest,
        source: 'landing_page',
      }),
    }).catch(() => {});

    setSubmitted(true);
    setFullName('');
    setEmail('');
    setInterest('SA Originals');
    setSubmitting(false);
  };

  return (
    <div style={{background:`radial-gradient(circle at 50% 0%, rgba(75,163,255,0.16) 0%, transparent 20%), radial-gradient(circle at 82% 8%, rgba(241,198,109,0.12) 0%, transparent 18%), linear-gradient(180deg,${C.bg} 0%, ${C.bg2} 42%, ${C.bg} 100%)`, color:C.white, fontFamily:'"SF Pro Display","SF Pro Text",-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif', overflowX:'hidden'}}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:${C.bg};overflow-x:hidden;}
        button:focus{outline:none;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-thumb{background:#55667d;border-radius:2px;}
        .poster-card,.feature-card,.stat-card,.pillar-card{transition:transform .28s ease, box-shadow .28s ease, border-color .28s ease;}
        .poster-card:hover,.feature-card:hover,.stat-card:hover,.pillar-card:hover{transform:translateY(-6px);box-shadow:0 24px 52px rgba(0,0,0,0.28);border-color:rgba(255,255,255,0.18);}
        .soft-input::placeholder{color:rgba(215,221,232,0.62);}
        @media(max-width:768px){
          .hero-title{font-size:clamp(36px,10vw,64px)!important;}
          .hero-sub{font-size:16px!important;}
          .stats-grid{grid-template-columns:repeat(2,1fr)!important;}
          .hero-grid{grid-template-columns:1fr!important;}
          .benefits-grid{grid-template-columns:1fr!important;}
          .shows-grid{grid-template-columns:1fr!important;}
          .poster-grid{grid-template-columns:repeat(2,1fr)!important;}
          .live-grid{grid-template-columns:1fr!important;}
          .shelves-grid{grid-template-columns:1fr!important;}
          .pillars-grid{grid-template-columns:1fr!important;}
          .earnings-grid{grid-template-columns:1fr!important;}
          .faq-grid{grid-template-columns:1fr!important;}
          .cta-buttons{flex-direction:column!important;}
          .nav-cta{display:none!important;}
          .hero-pad{padding:0 20px 80px!important;}
          .section-pad{padding:64px 20px!important;}
        }
        @media(min-width:769px) and (max-width:1180px){
          .pillars-grid{grid-template-columns:repeat(2,1fr)!important;}
        }
      `}</style>

      {/* NAV */}
      <nav style={{position:'fixed',top:14,left:24,right:24,zIndex:100,height:68,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 22px',background:'linear-gradient(180deg,rgba(19,26,36,0.72),rgba(17,23,32,0.54))',backdropFilter:'blur(28px) saturate(140%)',WebkitBackdropFilter:'blur(28px) saturate(140%)',border:`1px solid ${C.border}`,borderRadius:24,boxShadow:'0 16px 34px rgba(0,0,0,0.24)'}}>
        <div style={{fontSize:20,fontWeight:800,letterSpacing:6,color:C.white}}>VAULTT</div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button onClick={onSignIn} className="nav-cta" style={{padding:'8px 20px',background:'rgba(255,255,255,0.06)',color:C.white,border:`1px solid ${C.border}`,borderRadius:999,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Sign In</button>
          <button onClick={onApply} style={{padding:'8px 20px',background:'linear-gradient(135deg,#f2d089,#c89c4a)',color:'#17110a',border:'none',borderRadius:999,fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 10px 24px rgba(241,198,109,0.18)'}}>Apply as Creator</button>
        </div>
      </nav>

      {/* HERO */}
      <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',padding:'138px 5% 88px',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',inset:0,background:'radial-gradient(circle at 14% 10%,rgba(75,163,255,0.2) 0%,transparent 28%), radial-gradient(circle at 78% 8%,rgba(241,198,109,0.14) 0%,transparent 22%), radial-gradient(circle at 50% 0%,rgba(255,255,255,0.08) 0%,transparent 26%)',pointerEvents:'none'}}/>

        <div style={{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(255,255,255,0.07)',border:`1px solid ${C.border}`,borderRadius:20,padding:'6px 16px',fontSize:12,fontWeight:700,color:'#f7e7bf',letterSpacing:1.5,textTransform:'uppercase',marginBottom:28}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:C.gold,display:'inline-block'}}/>
          R49 launch offer for the first 6 months
        </div>

        <div className="hero-grid" style={{display:'grid',gridTemplateColumns:'minmax(0,1.15fr) minmax(320px,0.85fr)',gap:28,alignItems:'stretch',maxWidth:1180,width:'100%'}}>
          <div style={{textAlign:'left'}}>
            <h1 className="hero-title" style={{fontSize:'clamp(56px,7vw,102px)',fontWeight:900,lineHeight:0.92,letterSpacing:-3.6,marginBottom:24,maxWidth:840,textWrap:'balance',textShadow:'0 20px 40px rgba(0,0,0,0.22)'}}>
              Watch what<br/>
              <span style={{background:'linear-gradient(135deg,#ffffff 10%,#f2d089 55%,#8fc4ff 100%)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>South Africa</span><br/>
              is talking about.
            </h1>

            <p className="hero-sub" style={{fontSize:21,color:'rgba(231,237,245,0.9)',lineHeight:1.68,maxWidth:660,marginBottom:32,fontWeight:300,letterSpacing:-0.15}}>
              South African originals, creator-led formats and live moments are being lined up now. Join early at R49 per month for the first 6 months after launch.
            </p>

            <div className="cta-buttons" style={{display:'flex',gap:14,flexWrap:'wrap',marginBottom:24}}>
              <button onClick={onSignIn} style={{padding:'16px 36px',background:'linear-gradient(135deg,#ffffff,#edf4ff)',color:'#071018',border:'none',borderRadius:999,fontSize:16,fontWeight:800,cursor:'pointer',fontFamily:'inherit',transition:'all 0.2s',boxShadow:'0 14px 30px rgba(255,255,255,0.08)'}}>
                Join Early Access
              </button>
              <button onClick={onApply} style={{padding:'16px 36px',background:'rgba(255,255,255,0.07)',color:C.white,border:`1px solid ${C.border}`,borderRadius:999,fontSize:16,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
                Apply as Creator
              </button>
            </div>

            <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:28}}>
              {['Pre-launch access','Founding viewer price','South African originals','Launch updates'].map((t,i)=>(
                <span key={i} style={{padding:'8px 14px',background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:12,color:C.grey2}}>{t}</span>
              ))}
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,maxWidth:760}}>
              {[
                {label:'Founding viewer price', value:'R49'},
                {label:'Launch window', value:'June 2026'},
                {label:'Early access', value:'Open now'},
              ].map((item,i)=>(
                <div key={i} style={{background:'linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))',border:`1px solid ${C.border}`,borderRadius:18,padding:'18px 16px',boxShadow:'inset 0 1px 0 rgba(255,255,255,0.03)'}}>
                  <div style={{fontSize:27,fontWeight:800,color:C.white,marginBottom:6,letterSpacing:-0.6}}>{item.value}</div>
                  <div style={{fontSize:12,color:C.grey2,lineHeight:1.5}}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{background:'linear-gradient(160deg,rgba(255,255,255,0.09),rgba(75,163,255,0.06) 45%,rgba(255,255,255,0.03))',border:`1px solid ${C.border}`,borderRadius:32,padding:22,boxShadow:'0 30px 80px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.04)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:2.5,textTransform:'uppercase',color:C.grey2,marginBottom:4}}>Spotlight</div>
                <div style={{fontSize:29,fontWeight:800,color:C.white,letterSpacing:-0.8}}>Coming Soon</div>
              </div>
              <div style={{padding:'5px 10px',background:'rgba(241,198,109,0.12)',border:`1px solid rgba(241,198,109,0.24)`,borderRadius:999,fontSize:11,fontWeight:700,color:'#f7e1ab'}}>Pre-launch</div>
            </div>
            <div style={{height:280,borderRadius:22,background:'linear-gradient(135deg,#152235,#24385e 38%,#0b0d14 100%)',marginBottom:16,position:'relative',overflow:'hidden',display:'flex',alignItems:'flex-end',padding:20}}>
              <div style={{position:'absolute',inset:14,borderRadius:18,border:'1px solid rgba(255,255,255,0.08)'}}/>
              <div style={{position:'absolute',top:16,left:16,padding:'4px 10px',background:'rgba(255,255,255,0.12)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:11,fontWeight:800,color:C.white}}>COMING SOON</div>
              <div style={{position:'absolute',right:-16,top:18,width:150,height:150,borderRadius:'50%',background:'radial-gradient(circle,rgba(241,198,109,0.4),transparent 60%)',opacity:0.45,filter:'blur(2px)'}}/>
              <div style={{position:'absolute',left:32,top:58,width:110,height:110,borderRadius:'50%',background:'radial-gradient(circle,rgba(135,192,255,0.38),transparent 62%)',opacity:0.52}}/>
              <div style={{position:'absolute',right:32,bottom:42,fontSize:94,fontWeight:900,color:'rgba(255,255,255,0.08)',letterSpacing:-6,lineHeight:0.9}}>06</div>
              <div style={{position:'absolute',left:18,right:18,top:94,height:1,background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent)'}}/>
              <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,rgba(255,255,255,0.05),transparent 26%,rgba(0,0,0,0.4) 100%)'}}/>
              <div style={{position:'absolute',left:18,right:18,bottom:84,height:1,background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.22),transparent)'}}/>
              <div style={{position:'relative',zIndex:1}}>
                <div style={{fontSize:12,color:'rgba(255,255,255,0.72)',marginBottom:8,letterSpacing:0.3}}>Launch slate • SA Originals • More to be announced</div>
                <div style={{fontSize:42,fontWeight:900,color:C.white,letterSpacing:-2.2,lineHeight:0.92,marginBottom:10}}>A cinematic<br/>South African start.</div>
                <div style={{fontSize:19,color:C.white,lineHeight:1.45,maxWidth:340,fontWeight:650,letterSpacing:-0.35}}>The first VAULTT originals, podcasts and live moments are being lined up now. This spotlight slot will be revealed closer to launch.</div>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginBottom:16}}>
              <button onClick={onSignIn} style={{flex:1,padding:'13px 18px',background:'linear-gradient(135deg,#ffffff,#edf4ff)',color:'#0a0a0a',border:'none',borderRadius:999,fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:'inherit'}}>Join early access</button>
              <button onClick={onApply} style={{padding:'13px 18px',background:'rgba(255,255,255,0.08)',color:C.white,border:`1px solid ${C.border}`,borderRadius:999,fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Apply as Creator</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
              {[
                {label:'Status', value:'Coming soon'},
                {label:'Launch slate', value:'In progress'},
                {label:'Founding price', value:'R49/mo'},
              ].map((item,i)=>(
                <div key={i} style={{background:'rgba(255,255,255,0.05)',borderRadius:10,padding:'12px 10px'}}>
                  <div style={{fontSize:11,color:C.grey,marginBottom:4}}>{item.label}</div>
                  <div style={{fontSize:13,fontWeight:800,color:C.white}}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <Section style={{padding:'76px 5% 34px'}}>
        <div className="stats-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14}}>
          {STATS.map((s,i)=>(
            <div key={i} className="stat-card" style={{textAlign:'left',padding:'24px 22px',background:'linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.035))',border:`1px solid ${C.border}`,borderRadius:22}}>
              <div style={{fontSize:46,fontWeight:800,color:C.white,lineHeight:0.95,marginBottom:10,letterSpacing:-1.2}}>{s.val}</div>
              <div style={{fontSize:13,color:C.grey2,lineHeight:1.55}}>{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* BROWSE ROWS */}
      <Section style={{padding:'34px 5% 18px'}}>
        {BROWSE_ROWS.map((row, rowIndex) => (
          <div key={rowIndex} style={{marginBottom:44}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18,flexWrap:'wrap'}}>
              <div style={{fontSize:31,fontWeight:800,color:C.white,letterSpacing:-1.05}}>{row.title}</div>
              <span style={{padding:'5px 10px',background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:10,fontWeight:800,letterSpacing:1.2,textTransform:'uppercase',color:'#f3dfb4'}}>{row.badge}</span>
            </div>
            <div className="poster-grid" style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:18}}>
              {row.items.map((item, itemIndex) => (
                <div key={itemIndex} className="poster-card" style={{overflow:'hidden',borderRadius:28,border:`1px solid ${C.border}`,background:'linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.03))',boxShadow:'0 18px 38px rgba(0,0,0,0.2)'}}>
                  <div style={{height:340,background:item.accent,position:'relative',display:'flex',alignItems:'flex-end',padding:'18px 18px 20px'}}>
                    <div style={{position:'absolute',inset:14,borderRadius:20,border:'1px solid rgba(255,255,255,0.08)'}}/>
                    <div style={{position:'absolute',inset:'12px 12px auto 12px',height:92,borderRadius:22,background:'linear-gradient(180deg,rgba(255,255,255,0.18),rgba(255,255,255,0.02))',opacity:0.18,filter:'blur(1px)'}}/>
                    <div style={{position:'absolute',right:-24,bottom:-40,width:140,height:140,borderRadius:'50%',background:'radial-gradient(circle,rgba(255,255,255,0.18),transparent 68%)',opacity:0.28}}/>
                    <div style={{position:'absolute',left:22,top:82,width:84,height:84,borderRadius:'50%',background:item.aura}}/>
                    <div style={{position:'absolute',top:14,left:14,fontFamily:'Arial',fontSize:56,fontWeight:900,color:'rgba(255,255,255,0.92)',lineHeight:1,textShadow:'0 8px 18px rgba(0,0,0,0.25)'}}>{item.rank}</div>
                    <div style={{position:'absolute',top:16,right:16,padding:'5px 10px',background:'rgba(255,255,255,0.08)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:10,fontWeight:800,letterSpacing:1.2,color:C.white}}>{item.stamp}</div>
                    <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,rgba(255,255,255,0.05),transparent 26%,rgba(0,0,0,0.4) 100%)'}}/>
                    <div style={{position:'absolute',right:18,top:88,fontSize:54,fontWeight:900,color:'rgba(255,255,255,0.08)',letterSpacing:-4,lineHeight:0.88}}>{item.glyph}</div>
                    <div style={{position:'relative',zIndex:1}}>
                      <div style={{fontSize:12,color:'rgba(255,255,255,0.74)',letterSpacing:1.8,textTransform:'uppercase',marginBottom:8,fontWeight:700}}>{item.meta}</div>
                      <div style={{fontSize:25,fontWeight:800,color:C.white,letterSpacing:-0.7,marginBottom:6,lineHeight:1.02,textShadow:'0 10px 30px rgba(0,0,0,0.2)'}}>{item.title}</div>
                      <div style={{fontSize:14,color:'rgba(255,255,255,0.82)',marginBottom:12,letterSpacing:-0.1}}>{item.subtitle}</div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {['Original', 'Coming Soon'].slice(0, itemIndex % 2 === 0 ? 2 : 1).map((tag) => (
                          <span key={tag} style={{padding:'5px 10px',background:'rgba(255,255,255,0.08)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:10,fontWeight:700,color:C.white}}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>

      {/* FEATURE STRIPS */}
      <Section style={{padding:'20px 5% 90px'}}>
        <div className="benefits-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16}}>
          {FEATURE_STRIPS.map((item,i)=>(
            <div key={i} className="feature-card" style={{background:'linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))',border:`1px solid ${C.border}`,borderRadius:24,padding:'30px 24px',boxShadow:'inset 0 1px 0 rgba(255,255,255,0.03)'}}>
              <div style={{fontSize:11,fontWeight:800,letterSpacing:2.4,textTransform:'uppercase',color:i===1?C.blue:'#f1d7a0',marginBottom:14}}>{item.eyebrow}</div>
              <div style={{fontSize:31,fontWeight:800,color:C.white,letterSpacing:-1,marginBottom:12,lineHeight:1.02}}>{item.title}</div>
              <div style={{fontSize:15,color:C.grey2,lineHeight:1.75}}>{item.copy}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* TRUST & COPYRIGHT */}
      <Section style={{padding:'0 5% 90px'}}>
        <div style={{textAlign:'center',maxWidth:860,margin:'0 auto 28px'}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:3,textTransform:'uppercase',color:'#f1d7a0',marginBottom:14}}>Trust & Protection</div>
          <h2 style={{fontSize:'clamp(30px,4vw,48px)',fontWeight:800,letterSpacing:-1.1,lineHeight:1.02,marginBottom:14}}>Built for original creators, not copied content.</h2>
          <div style={{fontSize:16,color:C.grey2,lineHeight:1.75,maxWidth:700,margin:'0 auto'}}>VAULTT is being built with clear copyright rules, creator ownership protection and formal infringement reporting from day one.</div>
        </div>
        <div className="benefits-grid" style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:16}}>
          {TRUST_FEATURES.map((item,i)=>(
            <div key={i} className="feature-card" style={{background:'linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))',border:`1px solid ${C.border}`,borderRadius:24,padding:'30px 24px',boxShadow:'inset 0 1px 0 rgba(255,255,255,0.03)'}}>
              <div style={{fontSize:11,fontWeight:800,letterSpacing:2.4,textTransform:'uppercase',color:i===0?C.blue:'#f1d7a0',marginBottom:14}}>{item.eyebrow}</div>
              <div style={{fontSize:31,fontWeight:800,color:C.white,letterSpacing:-1,marginBottom:12,lineHeight:1.02}}>{item.title}</div>
              <div style={{fontSize:15,color:C.grey2,lineHeight:1.75,marginBottom:18}}>{item.copy}</div>
              {item.href ? (
                <a href={item.href} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:8,padding:'12px 18px',background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`,borderRadius:999,color:C.white,textDecoration:'none',fontSize:13,fontWeight:800}}>
                  {item.cta}
                </a>
              ) : (
                <button onClick={onApply} style={{padding:'12px 18px',background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`,borderRadius:999,color:C.white,textDecoration:'none',fontSize:13,fontWeight:800,cursor:'pointer',fontFamily:'inherit'}}>
                  {item.cta}
                </button>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* CONTENT PILLARS */}
      <Section style={{padding:'96px 5%',background:C.bg2,borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`}}>
        <div style={{textAlign:'center',maxWidth:900,margin:'0 auto 40px'}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:3,textTransform:'uppercase',color:'#f1d7a0',marginBottom:14}}>What&apos;s on VAULTT</div>
          <h2 style={{fontSize:'clamp(34px,4vw,56px)',fontWeight:800,letterSpacing:-1.6,lineHeight:0.96,marginBottom:16}}>Four lanes. One premium local platform.</h2>
          <div style={{fontSize:16,color:C.grey2,maxWidth:680,margin:'0 auto',lineHeight:1.75}}>VAULTT is being shaped around premium local formats with creator energy, strong repeat viewing and a clear point of view.</div>
        </div>
        <div className="pillars-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:18}}>
          {PILLARS.map((p,i)=>(
            <div key={i} className="pillar-card" style={{background:'linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0.028))',border:`1px solid ${C.border}`,borderRadius:26,padding:'26px 24px 24px',position:'relative',overflow:'hidden',minHeight:250}}>
              <div style={{position:'absolute',inset:0,background:p.accent,opacity:0.8,pointerEvents:'none'}}/>
              <div style={{position:'absolute',top:0,left:0,right:0,height:1,background:'linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,0.2),rgba(255,255,255,0))'}}/>
              <div style={{position:'relative',zIndex:1}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:28}}>
                  <div style={{fontSize:30}}>{p.icon}</div>
                  <span style={{padding:'6px 10px',background:'rgba(255,255,255,0.08)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:10,fontWeight:800,letterSpacing:1.2,textTransform:'uppercase',color:C.grey2}}>{p.tag}</span>
                </div>
                <div style={{fontSize:28,fontWeight:800,color:C.white,marginBottom:12,letterSpacing:-0.9,lineHeight:0.98}}>{p.title}</div>
                <div style={{fontSize:14,color:'rgba(215,221,232,0.9)',lineHeight:1.78}}>{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section style={{padding:'100px 5%',background:C.bg2,borderTop:`1px solid ${C.border}`}}>
        <div style={{textAlign:'center',marginBottom:56}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:3,textTransform:'uppercase',color:'#f1d7a0',marginBottom:16}}>FAQ</div>
          <h2 style={{fontSize:'clamp(32px,4vw,52px)',fontWeight:800,letterSpacing:-1.1}}>Everything you need to know.</h2>
        </div>
        <div style={{maxWidth:720,margin:'0 auto'}}>
          {FAQS.map((f,i)=>(
            <div key={i} style={{borderBottom:`1px solid ${C.border}`,overflow:'hidden'}}>
              <button onClick={()=>setOpenFaq(openFaq===i?null:i)} style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'20px 0',background:'transparent',border:'none',cursor:'pointer',fontFamily:'inherit',textAlign:'left'}}>
                <span style={{fontSize:15,fontWeight:700,color:C.white,paddingRight:16}}>{f.q}</span>
                <span style={{fontSize:20,color:C.white,flexShrink:0,transition:'transform 0.2s',transform:openFaq===i?'rotate(45deg)':'rotate(0)'}}>+</span>
              </button>
              {openFaq===i&&(
                <div style={{paddingBottom:20,fontSize:14,color:C.grey2,lineHeight:1.8}}>{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section style={{padding:'120px 5%',textAlign:'center',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse at 50% 50%,rgba(75,163,255,0.1) 0%,transparent 42%), radial-gradient(circle at 50% 62%, rgba(241,198,109,0.08) 0%, transparent 36%)',pointerEvents:'none'}}/>
        <div style={{position:'relative',zIndex:1}}>
          <div style={{fontSize:11,fontWeight:900,letterSpacing:7,color:C.white,marginBottom:24,opacity:0.9}}>VAULTT</div>
          <h2 style={{fontSize:'clamp(36px,5vw,68px)',fontWeight:800,marginBottom:20,lineHeight:1.02,letterSpacing:-1.5,maxWidth:840,marginInline:'auto'}}>Join the waitlist before VAULTT opens.</h2>
          <p style={{fontSize:18,color:C.grey2,maxWidth:640,margin:'0 auto 28px',lineHeight:1.7,fontWeight:300}}>Viewers get the founding price of R49 per month for the first 6 months after launch. Join early access now and we’ll keep you first in line for launch updates.</p>
          <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap',maxWidth:760,margin:'0 auto 18px'}}>
            <input
              value={fullName}
              onChange={e=>setFullName(e.target.value)}
              placeholder="Your name"
              className="soft-input"
              style={{flex:'1 1 220px',padding:'18px 18px',background:'rgba(255,255,255,0.08)',color:C.white,border:`1px solid ${C.border}`,borderRadius:16,fontSize:15,fontFamily:'inherit'}}
            />
            <input
              value={email}
              onChange={e=>setEmail(e.target.value)}
              placeholder="Enter your email for early access"
              className="soft-input"
              style={{flex:'1 1 320px',padding:'18px 18px',background:'rgba(255,255,255,0.08)',color:C.white,border:`1px solid ${C.border}`,borderRadius:16,fontSize:15,fontFamily:'inherit'}}
            />
            <select
              value={interest}
              onChange={e=>setInterest(e.target.value)}
              style={{flex:'1 1 220px',padding:'18px 18px',background:'rgba(255,255,255,0.08)',color:C.white,border:`1px solid ${C.border}`,borderRadius:16,fontSize:15,fontFamily:'inherit'}}
            >
              {['SA Originals', 'Sports', 'Talk Shows', 'Podcasts', 'Live Watch-Alongs'].map(option => (
                <option key={option} value={option} style={{background:'#111111', color:'#ffffff'}}>
                  {option}
                </option>
              ))}
            </select>
            <button
              onClick={handleEarlyAccess}
              disabled={submitting}
              style={{padding:'18px 28px',background:'linear-gradient(135deg,#ffffff,#edf4ff)',color:'#000000',border:'none',borderRadius:999,fontSize:16,fontWeight:800,cursor:'pointer',fontFamily:'inherit'}}
            >
              {submitting ? 'Saving...' : 'Get Early Access'}
            </button>
          </div>
          {submitError && <div style={{fontSize:13,color:C.red,marginBottom:18}}>{submitError}</div>}
          {submitted && <div style={{fontSize:13,color:C.green,marginBottom:18}}>You’re on the list for launch updates and early access news.</div>}
          <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap',marginBottom:18}}>
            {['R49 for first 6 months','Launch updates','South African originals','Live formats coming soon'].map((t,i)=>(
              <span key={i} style={{padding:'8px 14px',background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`,borderRadius:999,fontSize:12,color:C.grey2}}>{t}</span>
            ))}
          </div>
          <button onClick={onApply} style={{padding:'14px 32px',background:'rgba(255,255,255,0.06)',color:C.white,border:`1px solid ${C.border}`,borderRadius:999,fontSize:15,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
            Creator? Apply as Founding Creator
          </button>
        </div>
      </Section>

      {/* FOOTER */}
      <footer style={{borderTop:`1px solid ${C.border}`,padding:'32px 5%',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:16}}>
        <div style={{fontFamily:'Arial',fontSize:16,fontWeight:900,letterSpacing:5,color:C.white}}>VAULTT</div>
        <div style={{fontSize:12,color:C.grey}}>Where Africa Watches. &copy; 2026 VAULTT. All rights reserved.</div>
        <div style={{display:'flex',gap:20}}>
          <a
            href="/VAULTT_Terms_of_Service.pdf"
            target="_blank"
            rel="noreferrer"
            style={{fontSize:12,color:C.grey,cursor:'pointer',textDecoration:'none'}}
          >
            Terms
          </a>
          <a
            href="/VAULTT_Privacy_Policy.pdf"
            target="_blank"
            rel="noreferrer"
            style={{fontSize:12,color:C.grey,cursor:'pointer',textDecoration:'none'}}
          >
            Privacy
          </a>
          <a
            href="/copyright.html"
            target="_blank"
            rel="noreferrer"
            style={{fontSize:12,color:C.grey,cursor:'pointer',textDecoration:'none'}}
          >
            Copyright
          </a>
          <a
            href="mailto:creators@vaultt.co.za"
            style={{fontSize:12,color:C.grey,cursor:'pointer',textDecoration:'none'}}
          >
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}
